/**
 * Export evaluation results as JSON with pairwise comparisons
 */

import type { NameEntry, ExportData, ExportedCandidate, PairwiseComparison } from '../types'

/**
 * Generate pairwise comparisons from ranked candidates
 * For candidates ranked 1-5 (best to worst), generates all pairs where better < worse
 * Returns array of comparison objects with full candidate data (including scores)
 */
export const generatePairwiseComparisons = (
  original: string,
  ipa: string,
  rankedCandidates: ExportedCandidate[]
): PairwiseComparison[] => {
  const pairs: PairwiseComparison[] = []

  for (let i = 0; i < rankedCandidates.length; i++) {
    for (let j = i + 1; j < rankedCandidates.length; j++) {
      pairs.push({
        original,
        ipa,
        better: rankedCandidates[i],
        worse: rankedCandidates[j],
      })
    }
  }

  return pairs
}

/**
 * Build export data structure with evaluations
 */
export const buildExportData = (
  nameEntries: NameEntry[],
  evaluations: Record<string, NameEntry['candidates']>,
  evaluatorEmail: string
): ExportData => {
  const exportedNames = nameEntries.map((entry) => {
    // Use evaluated order if available, otherwise original order
    const candidates = evaluations[entry.id] || entry.candidates

    // Sort by current rank and map to export format (including score and normalizedScore if present)
    const sortedCandidates = [...candidates]
      .sort((a, b) => a.currentRank - b.currentRank)
      .map((c) => ({
        text: c.text,
        rank: c.currentRank,
        originalRank: c.originalRank,
        ...(c.score !== undefined && { score: c.score }), // Include score if present
        ...(c.normalizedScore !== undefined && { normalizedScore: c.normalizedScore }), // Include normalizedScore if present
      }))

    return {
      original: entry.original,
      ipa: entry.ipa,
      candidates: sortedCandidates,
      pairwiseComparisons: generatePairwiseComparisons(entry.original, entry.ipa, sortedCandidates),
    }
  })

  return {
    evaluated: true,
    timestamp: new Date().toISOString(),
    evaluator: evaluatorEmail,
    names: exportedNames,
  }
}

/**
 * Export as TSV format for training
 * Format: original\tipa\tbetter\tworse
 * Includes original name and IPA for use in re-ranking model training
 */
export const exportAsTSV = (nameEntries: NameEntry[], evaluations: Record<string, NameEntry['candidates']>): string => {
  const lines: string[] = []

  nameEntries.forEach((entry) => {
    const candidates = evaluations[entry.id]
    if (!candidates) return // Skip if not evaluated

    // Sort by current rank
    const sortedCandidates = [...candidates].sort((a, b) => a.currentRank - b.currentRank)

    // Generate pairwise comparisons with original and IPA
    for (let i = 0; i < sortedCandidates.length; i++) {
      for (let j = i + 1; j < sortedCandidates.length; j++) {
        const better = sortedCandidates[i].text
        const worse = sortedCandidates[j].text
        lines.push(`${entry.original}\t${entry.ipa}\t${better}\t${worse}`)
      }
    }
  })

  return lines.join('\n')
}

/**
 * Trigger browser download of blob
 */
const triggerDownload = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/**
 * Download evaluation results as JSON file
 */
export const downloadJSON = (
  nameEntries: NameEntry[],
  evaluations: Record<string, NameEntry['candidates']>,
  evaluatorEmail: string
): void => {
  const data = buildExportData(nameEntries, evaluations, evaluatorEmail)
  const jsonString = JSON.stringify(data, null, 2)
  const blob = new Blob([jsonString], { type: 'application/json' })
  triggerDownload(blob, `transliteration-evaluation-${Date.now()}.json`)
}

/**
 * Download evaluation results as TSV file
 */
export const downloadTSV = (nameEntries: NameEntry[], evaluations: Record<string, NameEntry['candidates']>): void => {
  const tsvContent = exportAsTSV(nameEntries, evaluations)
  const blob = new Blob([tsvContent], { type: 'text/tab-separated-values' })
  triggerDownload(blob, `comparisons-${Date.now()}.tsv`)
}
